import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  V3ChannelView,
  PublicCurrentBroadcast as apiBroadcast
} from 'api/v3/types/channel_view';
import { PublicCurrentBroadcast } from 'api/v3/types/refs/broadcast';

export interface Broadcast {
  uid: string;
  title: string;
  duration: number | null;
  categoryName: string;
  isLive: boolean;
  listenerCount: number | null;
  url: string;
  heartCount?: number;
  startedAt: string | null;
  eventId: string | null;
  finished: boolean;
  fallback: boolean | null;
  scheduled_recording: boolean | null;
}

export interface BroadcastsState {
  broadcasts: Broadcast[];
  lastAudioEndedTimestamp: number | null;
  heartLoading: boolean;
}

export const initialBroadcastsState: BroadcastsState = {
  broadcasts: [],
  lastAudioEndedTimestamp: null,
  heartLoading: false
};

const broadcastsSlice = createSlice({
  name: 'broadcasts',
  initialState: initialBroadcastsState,
  reducers: {
    addBroadcast(state, { payload: newBroadcast }: PayloadAction<Broadcast>) {
      const existingBroadcastIndex = state.broadcasts.findIndex(
        (broadcast) => broadcast.uid === newBroadcast.uid
      );

      if (existingBroadcastIndex >= 0) {
        const existingBroadcast = state.broadcasts[existingBroadcastIndex];
        if (
          !existingBroadcast.fallback &&
          !existingBroadcast.isLive &&
          existingBroadcast.finished
        ) {
          console.log(
            '[Broadcast Slice] - Existing broadcast is finished, not updating',
            JSON.stringify(existingBroadcast)
          );
          return;
        }
        state.broadcasts[existingBroadcastIndex] = {
          ...existingBroadcast,
          ...newBroadcast
        };
      } else {
        const filteredBroadcastsByEvent = state.broadcasts.filter(
          (broadcast) => broadcast.eventId !== newBroadcast.eventId
        );
        state.broadcasts = [{ ...newBroadcast }, ...filteredBroadcastsByEvent];
      }
    },
    addBroadcasts(
      state,
      { payload: newBroadcasts }: PayloadAction<Broadcast[]>
    ) {
      state.broadcasts = newBroadcasts;
    },
    updateBroadcast(
      state,
      { payload: updatedBroadcast }: PayloadAction<Partial<Broadcast>>
    ) {
      if (!updatedBroadcast.uid) {
        return;
      }

      const existingBroadcastIndex = state.broadcasts.findIndex(
        (broadcast) => broadcast.uid === updatedBroadcast.uid
      );

      const existingBroadcast = state.broadcasts[existingBroadcastIndex];

      state.broadcasts[existingBroadcastIndex] = {
        ...existingBroadcast,
        ...updatedBroadcast
      };
    },
    setAudioEndedTimestamp: (
      state,
      { payload: timestamp }: PayloadAction<number | null>
    ) => {
      state.lastAudioEndedTimestamp = timestamp;
    },
    setHeartLoading: (state, { payload: loading }: PayloadAction<boolean>) => {
      state.heartLoading = loading;
    }
  }
});

export const {
  addBroadcast,
  addBroadcasts,
  setAudioEndedTimestamp,
  setHeartLoading,
  updateBroadcast
} = broadcastsSlice.actions;

export const mapApiV3Broadcast = ({
  attributes: broadcast
}: PublicCurrentBroadcast): Broadcast => {
  return {
    uid: broadcast.uid.toString(),
    title: broadcast.title,
    duration: broadcast.duration,
    categoryName: broadcast.category_name,
    isLive: broadcast.live,
    url: broadcast.progressive_stream_url,
    heartCount: broadcast.heart_count,
    listenerCount: broadcast.listener_count,
    startedAt: broadcast.started_at,
    eventId: broadcast.event_id?.toString() || null,
    finished: false,
    fallback: broadcast.fallback || false,
    scheduled_recording: broadcast.scheduled_recording || false
  };
};

const mapApiBroadcast = (broadcast: apiBroadcast): Broadcast => {
  return mapApiV3Broadcast(broadcast);
};

export const mapV3ChannelViewToPublicBroadcasts = (
  channel: V3ChannelView
): Broadcast[] => {
  const broadcasts = channel.included.filter(
    (included): included is apiBroadcast => {
      return included.type === 'broadcast';
    }
  );

  return broadcasts.map(mapApiBroadcast);
};

export default broadcastsSlice.reducer;
